/**
 * Save activity - is anything of the user's still cooking?
 *
 * The server's word is the post's own `pipeline.status`, carried by every
 * posts endpoint. The client does not guess from enrichment flags or titles,
 * and does not read projections or mirrors of the pipeline.
 *
 * A pipeline that has not written for PIPELINE_SILENCE_LIMIT_MS is dead, not
 * in flight: the indicator goes quiet and polling stops. Marking such rows
 * failed is the pipeline's job (its reaper), not the client's.
 *
 * NORTHSTARS:
 * - One truth - pipeline.status, nothing else
 * - One language - the ellipsis means "the server says something is in flight"
 * - Pure - no DOM, no time of its own
 */

export const PIPELINE_SILENCE_LIMIT_MS = 2 * 60 * 60 * 1000;

/**
 * How often a visible, idle client asks whether new saves exist. Measured on
 * device 2026-08-27: at 60 s a save made elsewhere waited up to a full tick to
 * appear; the ids call is ~500 ms of ids only, so 20 s is cheap.
 */
export const SAVE_DISCOVERY_INTERVAL_MS = 20_000;

/** How often in-flight saves are re-read while the pipeline works on them. */
export const IN_FLIGHT_REFRESH_INTERVAL_MS = 5_000;

/** Long enough to cover every list's timer in one tick, short enough to miss no tick. */
const IN_FLIGHT_READ_WINDOW_MS = IN_FLIGHT_REFRESH_INTERVAL_MS - 1_000;

const IN_FLIGHT_STATUSES = new Set(["pending", "queued", "processing", "running"]);

export interface PipelineSnapshot {
  status?: unknown;
  updated_at?: unknown;
}

export interface PipelineCarrier {
  pipeline?: unknown;
  user_state?: unknown;
  unfurl_status?: unknown;
}

export interface SaveActivityPost extends PipelineCarrier {
  id: string;
}

const readPipeline = (post: PipelineCarrier): PipelineSnapshot | null => {
  const pipeline = post.pipeline;
  return pipeline && typeof pipeline === "object" ? (pipeline as PipelineSnapshot) : null;
};

/** Content the pipeline will never finish is not in flight, whatever its status says. */
const isRestricted = (post: PipelineCarrier): boolean =>
  post.user_state === "restricted" ||
  post.unfurl_status === "restricted" ||
  post.unfurl_status === "private" ||
  post.unfurl_status === "empty";

export const isPipelineInFlight = (post: PipelineCarrier, now: number): boolean => {
  if (isRestricted(post)) return false;
  const pipeline = readPipeline(post);
  if (!pipeline || typeof pipeline.status !== "string") return false;
  if (!IN_FLIGHT_STATUSES.has(pipeline.status)) return false;

  if (typeof pipeline.updated_at === "string") {
    const lastWrite = Date.parse(pipeline.updated_at);
    if (!Number.isNaN(lastWrite) && now - lastWrite > PIPELINE_SILENCE_LIMIT_MS) return false;
  }
  return true;
};

/**
 * Several lists can show one in-flight post, each on its own timer, and it
 * needs one read per interval. Returns the ids due a read and marks them read.
 */
export const claimInFlightReads = (
  ids: readonly string[],
  lastReadAt: Map<string, number>,
  now: number,
): string[] =>
  ids.filter(id => {
    const last = lastReadAt.get(id);
    if (last !== undefined && now - last < IN_FLIGHT_READ_WINDOW_MS) return false;
    lastReadAt.set(id, now);
    return true;
  });

export interface SaveActivity {
  /** Ids the pipeline is still working on, in display order. */
  inFlightIds: string[];
  /** Whether the indicator should show. */
  active: boolean;
}

export const resolveSaveActivity = (posts: readonly SaveActivityPost[], now: number): SaveActivity => {
  const inFlightIds = posts.filter(post => isPipelineInFlight(post, now)).map(post => post.id);
  return { inFlightIds, active: inFlightIds.length > 0 };
};

/**
 * The recent list asks for new saves on a fixed cadence while it is on
 * screen. It must not depend on how many pages are loaded: the list is
 * shared between Home and /stashes, and one scroll of /stashes would
 * otherwise switch discovery off for the rest of the session (measured on
 * device 2026-08-27: zero fetches in 45 s with two pages loaded). Re-reading
 * loaded pages costs one ids call per page.
 */
export const resolveSaveDiscoveryInterval = (): number => SAVE_DISCOVERY_INTERVAL_MS;
